/**
 * FrameProvider — the abstraction that makes SLAM debugging tractable.
 *
 * Backends never touch an HTMLVideoElement directly. They pull frames through
 * this interface, so the *same* tracking code can be driven by:
 *   - the live camera (VideoFrameProvider), on device
 *   - a recorded dataset (ReplayFrameProvider), at your desk, deterministically
 *
 * That second case is the whole point: a tracking failure recorded in a real
 * room becomes a reproducible, steppable bug instead of an anecdote.
 *
 * ## ⚠️ Replay is NOT bit-reproducible unless `Date.now` is pinned
 *
 * Measured 2026-10-05 (ADVIO-15, real vendored AlvaAR in Node): the same frames
 * through the same backend give a DIFFERENT trajectory on every run. AlvaAR
 * seeds its randomness (RANSAC during map init and tracking) from the wall-clock
 * date: patching only `Date.now` makes runs bit-identical, patching only
 * `performance.now` does not, and a different date diverges from map init
 * (~frame 18) onwards — e.g. the map restart point and the per-map scale change.
 * So one replay is ONE sample of a distribution, not "the" result for that
 * capture. The vendor cannot be edited (GPLv3, vendored as-is).
 *
 * Everything on OUR side of the seam is already deterministic given the frames:
 * `AlvaARBackend` times placement, scene depth and the walking hint on
 * `timestamp()` (the frame clock), never on the wall clock, and uses
 * `deps.now` only to measure latency and rate-limit logs. `SlamTelemetry`'s
 * `t`/`fps` take an injectable clock for the same reason.
 *
 * To make a replay reproducible, pin `Date.now` while stepping —
 * `withPinnedDateNow` below, called around each `step()` with a fixed base plus
 * the frame's timestamp. That is exactly what the offline evaluation harness
 * does (`run_alva.mts --clock dateonly --date-base <B>`, `Date.now = () =>
 * floor(B + frameTimeMs)`), treating each base as one seed; the ADVIO results
 * are reported over 5-10 such seeds. (ReplayApp does not do this yet.)
 */

export interface FrameProvider {
  ready(): boolean;
  /** Natural frame dimensions, or null if not ready. */
  size(): { width: number; height: number } | null;
  /**
   * Draw the current frame into ctx scaled to w×h. Returns false if no frame is
   * available this call.
   */
  drawTo(ctx: CanvasRenderingContext2D, w: number, h: number): boolean;
  /** Monotonic-ish timestamp of the current frame, ms. */
  timestamp(): number;
  /** Monotonically increasing id; unchanged means the same frame. */
  frameId(): number;
}

/**
 * Run `fn` (synchronously) with `Date.now()` returning `Math.floor(epochMs)`,
 * then restore it — even if `fn` throws. For replay only: it makes the
 * vendored tracker's date-seeded randomness a function of the frame, so the
 * same dataset with the same base reproduces bit for bit (see the note above).
 * Never call it on the live path; nothing live should see a frozen clock.
 */
export function withPinnedDateNow<T>(epochMs: number, fn: () => T): T {
  const real = Date.now;
  const pinned = Math.floor(epochMs);
  Date.now = () => pinned;
  try {
    return fn();
  } finally {
    Date.now = real;
  }
}

/** Live camera. */
export class VideoFrameProvider implements FrameProvider {
  private video: HTMLVideoElement | null;
  private lastTime = -1;
  private id = 0;

  constructor(video: HTMLVideoElement | null) {
    this.video = video;
  }

  setVideo(video: HTMLVideoElement | null): void {
    this.video = video;
    this.lastTime = -1;
  }

  ready(): boolean {
    const v = this.video;
    return !!v && v.readyState >= 2 && v.videoWidth > 0;
  }

  size(): { width: number; height: number } | null {
    const v = this.video;
    return this.ready() && v ? { width: v.videoWidth, height: v.videoHeight } : null;
  }

  drawTo(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
    const v = this.video;
    if (!v || !this.ready()) return false;
    // Only count a new frame when the media time advanced.
    if (v.currentTime !== this.lastTime) {
      this.lastTime = v.currentTime;
      this.id++;
    }
    ctx.drawImage(v, 0, 0, w, h);
    return true;
  }

  timestamp(): number {
    return performance.now();
  }

  frameId(): number {
    return this.id;
  }
}

/**
 * Recorded dataset playback. Holds decoded bitmaps and exposes one at a time,
 * driven externally by seek()/next() so replay is fully deterministic.
 */
export class ReplayFrameProvider implements FrameProvider {
  private frames: ImageBitmap[] = [];
  private times: number[] = [];
  private index = 0;

  constructor(frames: ImageBitmap[], times: number[]) {
    this.frames = frames;
    this.times = times;
  }

  get length(): number {
    return this.frames.length;
  }

  get position(): number {
    return this.index;
  }

  seek(i: number): void {
    this.index = Math.max(0, Math.min(this.frames.length - 1, i));
  }

  /** Advance one frame; returns false at the end. */
  next(): boolean {
    if (this.index >= this.frames.length - 1) return false;
    this.index++;
    return true;
  }

  ready(): boolean {
    return this.frames.length > 0;
  }

  size(): { width: number; height: number } | null {
    const f = this.frames[this.index];
    return f ? { width: f.width, height: f.height } : null;
  }

  drawTo(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
    const f = this.frames[this.index];
    if (!f) return false;
    ctx.drawImage(f, 0, 0, w, h);
    return true;
  }

  timestamp(): number {
    return this.times[this.index] ?? this.index * 33;
  }

  frameId(): number {
    return this.index;
  }
}
